import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { FaClock, FaEye } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import ReminderPreview from "./ReminderPreview";
import "./ReminderSettings.css";

// Settings > Notifications: automatic reminders before the closing date, each
// with its status line worked out by the server ("Will send on ...",
// "Sent to N people on ...", "Time has passed, will not send").
export default function ReminderSettings({ formId }) {
    const [view, setView] = useState(null);
    const [saving, setSaving] = useState(null);
    const [previewing, setPreviewing] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/reminders/${formId}/settings`, { method: "GET" });
            if (res.ok) setView(await res.json());
        } catch (err) {
            // the card simply doesn't show
        }
    }, [formId]);

    useEffect(() => { if (formId) load(); }, [formId, load]);

    const toggle = async (kind, on) => {
        setSaving(kind);
        try {
            const res = await apiFetch(`/api/reminders/${formId}/settings`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ [kind]: on }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                Swal.fire("Not saved", body.error || "Could not save the reminder setting.", "warning");
                return;
            }
            setView(body);
        } finally {
            setSaving(null);
        }
    };

    if (!view) return null;
    const noClosing = !view.closing;

    return (
        <div className="notification-card reminder-card">
            {previewing && <ReminderPreview formId={formId} onClose={() => setPreviewing(false)} />}
            <div className="notification-content reminder-card-head">
                <FaClock className="notification-icon" />
                <div className="notification-text">
                    <div className="reminder-title-row">
                        <strong>Automatic reminders</strong>
                        <button type="button" className="reminder-preview-btn" title="Preview the reminder email" onClick={() => setPreviewing(true)}>
                            <FaEye aria-hidden="true" /> Preview email
                        </button>
                    </div>
                    <p>
                        Email everyone who hasn't responded yet, at 9:00 AM India time (IST).{" "}
                        {!noClosing && `The form closes on ${view.closing_text}.`}
                    </p>
                </div>
            </div>

            {noClosing && (
                <div className="reminder-needs-date" role="note">
                    Please add a form expiration date first (Settings &gt; Access &gt; Form expiration date).
                    Reminders are sent before that date.
                </div>
            )}

            <div className="reminder-kinds">
                {view.kinds.map(k => (
                    <div key={k.kind} className="reminder-kind" data-kind={k.kind}>
                        <div className="reminder-kind-text">
                            <span className="reminder-kind-label">{k.label}</span>
                            <span className={`reminder-status reminder-status-${k.state}`}>{k.status}</span>
                        </div>
                        <label className={`switch${k.locked ? " reminder-switch-locked" : ""}`} title={k.locked ? k.status : ""}>
                            <input
                                type="checkbox"
                                aria-label={k.label}
                                checked={k.on && !k.locked}
                                disabled={noClosing || k.locked || saving === k.kind}
                                onChange={() => toggle(k.kind, !k.on)}
                            />
                            <span className="slider round"></span>
                        </label>
                    </div>
                ))}
            </div>
        </div>
    );
}
